import { DestroyRef, effect, inject, Injectable } from '@angular/core';
import { Subscription, Transmit } from '@adonisjs/transmit-client';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { AccountService } from '@core/auth';
import { environment } from '@environments/environment';
import { NotificationsService } from './notifications';

type NotificationPayload = {
  title?: string;
  message?: string;
};

@Injectable({ providedIn: 'root' })
export class NotificationsStreamService {
  private readonly accountService = inject(AccountService);
  private readonly oidc = inject(OidcSecurityService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly destroyRef = inject(DestroyRef);

  private transmit: Transmit | null = null;
  private subscription: Subscription | null = null;

  constructor() {
    effect(() => {
      const profile = this.accountService.profile;

      if (profile()) {
        void this.connect(profile()!.m3Id);
      } else {
        void this.disconnect();
      }
    });

    this.destroyRef.onDestroy(() => void this.disconnect());
  }

  private async connect(userId: string) {
    if (this.subscription) return;

    const token = await firstValueFrom(this.oidc.getAccessToken());
    if (!token) return;

    this.transmit = new Transmit({
      baseUrl: environment.api + '/employee/v2',
      beforeSubscribe: (request) => {
        request.headers.set('Authorization', `Bearer ${token}`);
      },
      beforeUnsubscribe: (request) => {
        request.headers.set('Authorization', `Bearer ${token}`);
      },
    });

    this.subscription = this.transmit.subscription(`notifications/${userId}`);
    await this.subscription.create();
    this.subscription.onMessage<NotificationPayload>((data) => {
      this.notificationsService.changed();
      this.playSound();
      this.showBrowserNotification(data);
    });
  }

  private playSound() {
    // Audio playback is best-effort: browsers may block autoplay until the
    // user has interacted with the page. Swallow those errors.
    try {
      void new Audio('/sounds/notification.wav').play().catch(() => undefined);
    } catch {
      // ignore
    }
  }

  /**
   * Asks the browser for permission to show desktop notifications.
   * Call from a user-gesture handler (e.g. when the user opens the bell)
   * so browsers don't silently block the prompt.
   */
  ensurePermission() {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'default') return;
    void Notification.requestPermission();
  }

  private showBrowserNotification({ title, message }: NotificationPayload) {
    if (!('Notification' in window) || !title) return;
    if (Notification.permission !== 'granted') return;

    new Notification(title, {
      body: message,
      icon: '/web-app-manifest-192x192.png',
      badge: '/favicon-96x96.png',
    });
  }

  private async disconnect() {
    if (this.subscription) {
      try {
        await this.subscription.delete();
      } catch {
        // ignore
      }
      this.subscription = null;
    }
    if (this.transmit) {
      this.transmit.close();
      this.transmit = null;
    }
  }
}
